import { fetch_accessible_platforms, launch_platform } from './platform_access_service';
import { FALLBACK_PLATFORM_COLOR, get_platform_config } from './platforms_config';

const GRID_COLUMNS = 3;

export default {
  name: 'PlatformSwitcher',
  props: {
    current_platform_id: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    is_loading: true,
    has_error: false,
    is_open: false,
    platforms: [],
    active_index: -1,
    launching_id: null,
    launch_error: '',
  }),
  computed: {
    is_aic_one_user() {
      return (sessionStorage.getItem('is_aic_one_login') || '').toLowerCase() === 'true';
    },
    current_platform() {
      return get_platform_config(this.current_platform_id) || {
        id: this.current_platform_id,
        name: this.current_platform_id,
        monogram: '?',
        color: FALLBACK_PLATFORM_COLOR,
      };
    },
    sorted_platforms() {
      return [...this.platforms].sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  mounted() {
    if (!this.is_aic_one_user) return;
    document.addEventListener('click', this.on_outside_click);
    window.addEventListener('pageshow', this.handle_pageshow);
    this.fetch_platforms();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.on_outside_click);
    window.removeEventListener('pageshow', this.handle_pageshow);
  },
  methods: {
    async fetch_platforms() {
      this.is_loading = true;
      this.has_error = false;
      try {
        this.platforms = await fetch_accessible_platforms(this.current_platform_id);
      } catch (_error) {
        this.has_error = true;
        this.platforms = [];
      } finally {
        this.is_loading = false;
      }
    },
    retry_fetch() {
      this.fetch_platforms();
    },
    badge_style: (platform) => ({
      backgroundColor: platform?.color || FALLBACK_PLATFORM_COLOR,
      color: '#fff',
    }),
    tile_style: (platform) => ({ '--tile-color': platform?.color || FALLBACK_PLATFORM_COLOR }),
    is_current_platform(platform) {
      return platform.id === this.current_platform_id;
    },
    toggle_menu() {
      if (this.is_loading) return;
      if (this.is_open) this.close_menu();
      else this.open_menu();
    },
    open_menu() {
      if (this.is_loading) return;
      this.is_open = true;
      this.launch_error = '';
      this.active_index = -1;
      this.$nextTick(() => this.focus_item(0));
    },
    close_menu(refocus_trigger = false) {
      this.is_open = false;
      this.active_index = -1;
      if (refocus_trigger) this.$refs.trigger_button?.focus();
    },
    menu_item_refs() {
      const refs = this.$refs.platform_tile;
      if (Array.isArray(refs)) return refs;
      return refs ? [refs] : [];
    },
    focus_item(index) {
      const items = this.menu_item_refs();
      if (!items.length) return;
      const wrapped_index = (index + items.length) % items.length;
      this.active_index = wrapped_index;
      items[wrapped_index]?.focus();
    },
    on_menu_keydown(event) {
      const key_actions = {
        Escape: () => this.close_menu(true),
        ArrowRight: () => this.focus_item(this.active_index + 1),
        ArrowLeft: () => this.focus_item(this.active_index - 1),
        ArrowDown: () => this.focus_item(this.active_index + GRID_COLUMNS),
        ArrowUp: () => this.focus_item(this.active_index - GRID_COLUMNS),
        Home: () => this.focus_item(0),
        End: () => this.focus_item(this.menu_item_refs().length - 1),
      };
      const action = key_actions[event.key];
      if (!action) return;
      event.preventDefault();
      action();
    },
    async select_platform(platform) {
      if (this.is_current_platform(platform)) {
        this.close_menu(true);
        return;
      }
      if (this.launching_id) return;
      this.launch_error = '';
      this.launching_id = platform.id;
      try {
        await launch_platform(platform);
      } catch (error) {
        this.launch_error = error.message || 'Unable to launch platform';
        this.launching_id = null;
      }
    },
    on_outside_click(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) this.close_menu();
    },
    handle_pageshow(event) {
      if (!event.persisted) return;
      this.launching_id = null;
      this.launch_error = '';
    },
  },
};
